/**
 * AI Organize API.
 *
 * The LLM endpoint is configured on the server and proxied through
 * `POST /api/ai/organize`, which streams Server-Sent Events back. The API key
 * is write-only from the browser's perspective, exactly like the storage
 * secret key.
 */
import { api, ApiError, toError } from './client.js';

const BASE = '/api';

/**
 * The LLM settings shown in the UI.
 *
 * `apiKey` is deliberately absent: the server only reports whether one is
 * stored, so a live key never reaches the browser.
 */
export interface LLMSettingsView {
  baseURL: string;
  model: string;
  hasApiKey: boolean;
  configured: boolean;
  /** When false, saved settings are lost on restart. */
  persist: boolean;
}

export interface LLMSettingsInput {
  baseURL: string;
  /** Leave empty to keep the stored key unchanged. */
  apiKey: string;
  model: string;
}

export interface LLMTestResult {
  ok: boolean;
  message: string;
}

/** Live status while waiting for the first content token. */
export type OrganizeStatusKind = 'waiting' | 'reasoning';

export interface OrganizeStreamHandlers {
  /** Emitted for the waiting/thinking phases, before any content arrives. */
  onStatus?: (kind: OrganizeStatusKind) => void;
  /** Emitted with the accumulated Markdown body as tokens arrive. */
  onContent?: (content: string) => void;
}

export const llmApi = {
  /** Current AI Organize settings. Requires a session. */
  getSettings: () => api.get<{ llm: LLMSettingsView }>('/settings/llm'),

  /**
   * Saves the endpoint config and applies it immediately. A blank API key
   * keeps the stored one.
   */
  saveSettings: (input: LLMSettingsInput) =>
    api.put<{ ok: true; llm: LLMSettingsView }>(
      '/settings/llm',
      JSON.stringify(input),
      'application/json; charset=utf-8',
    ),

  /** Proves the stored endpoint answers with one tiny completion. */
  test: () => api.post<LLMTestResult>('/ai/test', {}),
};

/** Thrown when the proxy stream ends with an `error` event. */
export class OrganizeStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OrganizeStreamError';
  }
}

/**
 * Streams an AI Organize rewrite of `content`.
 *
 * Resolves with the final body once the server sends `done`; rejects with an
 * ApiError (pre-stream validation) or OrganizeStreamError (mid-stream
 * failure). Aborting `signal` cancels the fetch — the server in turn cancels
 * the upstream request.
 */
export async function organizeStream(
  content: string,
  handlers: OrganizeStreamHandlers = {},
  signal?: AbortSignal,
): Promise<string> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/ai/organize`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
      signal,
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new ApiError(0, 'network', `Could not reach the server. ${(error as Error).message}`);
  }

  if (!response.ok) {
    throw await toError(response);
  }
  if (!response.body) {
    throw new OrganizeStreamError('The server returned an empty stream.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let final: string | null = null;
  let failure: OrganizeStreamError | null = null;

  const onLine = (line: string): void => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) return;
    let event: Record<string, unknown>;
    try {
      event = JSON.parse(trimmed.slice(5).trim()) as Record<string, unknown>;
    } catch {
      return;
    }
    if (event.type === 'status') {
      handlers.onStatus?.(event.status === 'reasoning' ? 'reasoning' : 'waiting');
    } else if (event.type === 'content') {
      if (typeof event.content === 'string') handlers.onContent?.(event.content);
    } else if (event.type === 'done') {
      if (typeof event.content === 'string') final = event.content;
    } else if (event.type === 'error') {
      failure = new OrganizeStreamError(
        typeof event.message === 'string' ? event.message : 'AI Organize failed.',
      );
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let newline: number;
      while ((newline = buffer.indexOf('\n')) !== -1) {
        onLine(buffer.slice(0, newline));
        buffer = buffer.slice(newline + 1);
      }
    }
    const tail = buffer + decoder.decode();
    if (tail.trim()) onLine(tail);
  } finally {
    reader.releaseLock();
  }

  if (failure) throw failure;
  if (final === null) {
    throw new OrganizeStreamError('The stream ended before the rewrite finished.');
  }
  return final;
}
